/**
 * Field Service
 *
 * 案場管理服務
 */

import { customAlphabet } from 'nanoid'
import sqlService from '../core/sqlService'
import accessService from '../auth/accessService'
import relationshipService from '../organization/relationshipService'

// 使用自定義字母表生成類似 Firebase 的 ID
const nanoid = customAlphabet('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789', 20)

// ==================== 類型定義 ====================

/** 案場狀態 */
export type FieldStatus = 'active' | 'normal' | 'error' | 'deleted' | 'inactive' | 'maintenance'

/** 地理位置 */
export interface Location {
  latitude: number | null
  longitude: number | null
}

/** 案場資料庫 row */
export interface FieldRow {
  id: string
  name: string
  shortName?: string | null
  address?: string | null
  latitude?: number | null
  longitude?: number | null
  contact_person?: string | null
  contact_phone?: string | null
  notes?: string | null
  image?: string | null
  type: string | string[]
  status: FieldStatus
  com_name?: string | null
  com_number?: string | null
  city_code?: string | null
  city?: string | null
  capacity?: number | null
  organization_id?: string | null
  created_by?: string | null
  created_at?: string
  updated_at?: string
  deleted_at?: string | null
  field_id?: string // 舊 ID（遷移用）
  risk_score?: number | null
  risk_level?: string | null
}

/** 轉換後的案場資料 */
export interface TransformedField {
  id: string
  name: string
  address?: string | null
  image?: string | null
  type: string[]
  status: FieldStatus
  created_at?: string
  updated_at?: string
  created_by?: string | null
  organization_id?: string | null
  latitude?: number | null
  longitude?: number | null
  lat?: number | null // 簡寫（for 地圖組件）
  lng?: number | null // 簡寫（for 地圖組件）
  location: Location | null
  city?: string | null
  capacity?: number | null
  contact_person?: string | null
  contact_phone?: string | null
  notes?: string | null
  shortName?: string | null
  com_name?: string | null
  com_number?: string | null
  city_code?: string | null
  risk_score?: number | null
  risk_level?: string | null
}

/** 建立案場參數 */
export interface CreateFieldParams {
  name?: string
  shortName?: string
  address?: string
  type?: string[]
  latitude?: number | null
  longitude?: number | null
  contactPerson?: string | null
  contactPhone?: string | null
  notes?: string | null
  status?: FieldStatus
  com_name?: string | null
  com_number?: string | null
  city_code?: string | null
  createdBy?: string | null
}

/** 更新案場參數 */
export interface UpdateFieldParams {
  name?: string
  shortName?: string
  address?: string
  latitude?: number | null
  longitude?: number | null
  contactPerson?: string | null
  contactPhone?: string | null
  notes?: string | null
  image?: string | null
  type?: string[]
  status?: FieldStatus
  com_name?: string | null
  com_number?: string | null
  city_code?: string | null
}

/** 欄位映射表（fieldData key → DB column） */
interface FieldMapping {
  [dbField: string]: string
}

// ==================== Service 實作 ====================

export const fieldService = {
  /**
   * 建立新案場
   */
  async createField(fieldData: CreateFieldParams, orgId: string): Promise<{ id: string }> {
    if (!orgId) {
      throw new Error('組織 ID 不能為空')
    }

    const fieldId = nanoid()
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ')

    try {
      await sqlService.beginTransaction()

      // 插入案場基本資料，包含所有欄位
      const sql = `
        INSERT INTO fields (
          id, name, shortName, address, type,
          latitude, longitude, contact_person, contact_phone,
          notes, status, com_name, com_number, city_code,
          created_at, updated_at, created_by, organization_id
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
      `

      const values: (string | number | null)[] = [
        fieldId,
        fieldData.name || '',
        fieldData.shortName || '',
        fieldData.address || '',
        JSON.stringify(fieldData.type || []),
        fieldData.latitude ?? null,
        fieldData.longitude ?? null,
        fieldData.contactPerson ?? null,
        fieldData.contactPhone ?? null,
        fieldData.notes ?? null,
        fieldData.status || 'active',
        fieldData.com_name ?? null,
        fieldData.com_number ?? null,
        fieldData.city_code ?? null,
        now,
        now,
        fieldData.createdBy ?? null,
        orgId
      ]

      await sqlService.query(sql, values)

      // 設置組織-案場關係
      await relationshipService.setRole(orgId, fieldId, '', ['owner'])

      await sqlService.commit()
      return { id: fieldId }
    } catch (error) {
      await sqlService.rollback()
      throw error
    }
  },

  /**
   * 取得案場資料
   */
  async getField(fieldId: string): Promise<TransformedField | null> {
    if (!fieldId) {
      throw new Error('案場 ID 不能為空')
    }

    try {
      // 查詢案場基本資料
      const sql = `
        SELECT *
        FROM fields
        WHERE id = ?
        AND deleted_at IS NULL
      `

      const results = await sqlService.query(sql, [fieldId]) as FieldRow[]
      if (!results || !Array.isArray(results) || results.length === 0) {
        console.log('未找到案場:', fieldId)
        return null
      }

      const field = this.transformFieldData(results[0])

      // 如果 type 已經是物件，不需要再解析
      if (typeof field.type === 'string') {
        try {
          field.type = JSON.parse(field.type || '[]')
        } catch (e) {
          console.error('解析案場類型數據時發生錯誤:', e)
          field.type = []
        }
      }

      return field
    } catch (error) {
      console.error(`獲取案場資料時發生錯誤 (${fieldId}):`, error)
      throw error
    }
  },

  /**
   * 取得組織的所有案場
   */
  async getFields(organizationId: string): Promise<TransformedField[]> {
    const sql = `
      SELECT * FROM fields
      WHERE organization_id = ? AND deleted_at IS NULL
      ORDER BY created_at DESC
    `

    const results = await sqlService.query(sql, [organizationId]) as FieldRow[]
    return results.map(row => this.transformFieldData(row))
  },

  /**
   * 透過預存程序取得組織的所有案場
   */
  async getFieldsByOrganization(organizationId: string): Promise<TransformedField[]> {
    const results = await sqlService.callProcedure('GetOrganizationFields', [organizationId])

    // MySQL 預存程序返回的結果在第一個元素中
    // callProcedure 在新架構下返回 [data]，所以 results[0] 是資料陣列
    const fields = Array.isArray(results[0]) ? results[0] : results
    return (fields as FieldRow[]).map(row => this.transformFieldData(row))
  },

  /**
   * 設定案場為錯誤狀態
   */
  async setErrorStatus(fieldId: string): Promise<void> {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const sql = `
      UPDATE fields
      SET status = 'error',
          type = JSON_ARRAY_APPEND(
              COALESCE(type, '[]'),
              '$',
              'ERROR'
          ),
          updated_at = ?
      WHERE id = ?
    `

    await sqlService.query(sql, [now, fieldId])
  },

  /**
   * 清除案場的錯誤狀態
   */
  async clearErrorStatus(fieldId: string): Promise<void> {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const sql = `
      UPDATE fields
      SET status = 'normal',
          type = JSON_REMOVE(
              type,
              JSON_UNQUOTE(JSON_SEARCH(type, 'one', 'ERROR'))
          ),
          updated_at = ?
      WHERE id = ?
    `

    await sqlService.query(sql, [now, fieldId])
  },

  /**
   * 更新案場資料
   */
  async updateField(fieldId: string, fieldData: UpdateFieldParams): Promise<void> {
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const updates: string[] = []
    const values: (string | number | null)[] = []

    // 定義所有可能的欄位及其在 fieldData 中的鍵
    const possibleFields: FieldMapping = {
      'name': 'name',
      'shortName': 'shortName',
      'address': 'address',
      'latitude': 'latitude',
      'longitude': 'longitude',
      'contact_person': 'contactPerson',
      'contact_phone': 'contactPhone',
      'notes': 'notes',
      'image': 'image',
      'type': 'type',
      'status': 'status',
      'com_name': 'com_name',
      'com_number': 'com_number',
      'city_code': 'city_code',
    }

    // 動態建構更新欄位
    for (const dbField in possibleFields) {
      const dataKey = possibleFields[dbField] as keyof UpdateFieldParams
      if (fieldData[dataKey] !== undefined) {
        updates.push(`${dbField} = ?`)
        if (dbField === 'type') {
          values.push(JSON.stringify(fieldData[dataKey]))
        } else {
          values.push(fieldData[dataKey] as string | number | null)
        }
      }
    }

    if (updates.length === 0) {
      console.log('沒有需要更新的欄位')
      return
    }

    updates.push('updated_at = ?')
    values.push(now)

    // 添加 WHERE 條件的參數
    values.push(fieldId)

    const sql = `
      UPDATE fields
      SET ${updates.join(', ')}
      WHERE id = ?
    `

    await sqlService.query(sql, values)
  },

  /**
   * 刪除案場（軟刪除）
   */
  async deleteField(fieldId: string): Promise<void> {
    if (!(await accessService.canPerformAction(
      'field:delete_field',
      null,
      fieldId,
      accessService.getUserId()
    ))) {
      throw new Error('權限不足：無法刪除案場')
    }

    const now = new Date().toISOString().slice(0, 19).replace('T', ' ')
    const sql = `
      UPDATE fields
      SET deleted_at = ?,
          status = 'deleted'
      WHERE id = ?
    `

    await sqlService.query(sql, [now, fieldId])
  },

  /**
   * 從組織移除案場
   */
  async removeFieldFromOrganization(orgId: string, fieldId: string): Promise<void> {
    if (!(await accessService.canPerformAction(
      'field:delete_field',
      orgId,
      fieldId,
      accessService.getUserId()
    ))) {
      throw new Error('權限不足：無法刪除案場')
    }

    try {
      await sqlService.beginTransaction()

      // 軟刪除案場
      await this.deleteField(fieldId)

      // 刪除相關的關係數據
      await relationshipService.removeAllFieldRelationships(fieldId)

      await sqlService.commit()
    } catch (error) {
      await sqlService.rollback()
      throw error
    }
  },

  /**
   * 轉換資料庫 row 為前端格式
   */
  transformFieldData(row: FieldRow): TransformedField {
    // 構建 location 對象（如果有座標）
    const location: Location | null = (row.latitude && row.longitude) ? {
      latitude: row.latitude,
      longitude: row.longitude
    } : null

    // 處理 type 欄位
    let typeValue: string[] = []
    if (typeof row.type === 'string') {
      try {
        typeValue = JSON.parse(row.type)
      } catch {
        typeValue = []
      }
    } else if (Array.isArray(row.type)) {
      typeValue = row.type
    }

    return {
      id: row.id,
      name: row.name,
      address: row.address,
      image: row.image,
      type: typeValue,
      status: row.status,
      created_at: row.created_at,
      updated_at: row.updated_at,
      created_by: row.created_by,
      organization_id: row.organization_id,
      // 完整欄位名
      latitude: row.latitude,
      longitude: row.longitude,
      // 簡寫欄位名（for 地圖組件）
      lat: row.latitude,
      lng: row.longitude,
      // location 對象（for view.js 檢查）
      location: location,
      // 其他欄位
      city: row.city,
      capacity: row.capacity,
      contact_person: row.contact_person,
      contact_phone: row.contact_phone,
      notes: row.notes,
      shortName: row.shortName,
      com_name: row.com_name,
      com_number: row.com_number,
      city_code: row.city_code,
      risk_score: row.risk_score ?? null,
      risk_level: row.risk_level ?? null,
    }
  },

  /**
   * 檢查舊 ID 是否存在
   */
  async checkByOldId(oldId: string): Promise<boolean> {
    const sql = `
      SELECT COUNT(*) as count
      FROM fields
      WHERE field_id = ? AND deleted_at IS NULL
    `

    const result = await sqlService.query(sql, [oldId]) as Array<{ count: number }>
    return result[0].count > 0
  }
}

export default fieldService
